import React from 'react';
import { motion } from 'framer-motion';

const team = [
  {
    name: "Sarthak Shah",
    role: "CEO & Co-Founder",
    image: "/images/sarthak.png",
    description: "Cybersecurity and AI development expertise, specializing in security architecture and threat detection algorithms for clinical environments.",
    featured: true
  },
  {
    name: "Siyethaba Nxumalo",
    role: "COO & Co-Founder",
    image: "/images/siyethaba.png",
    description: "2x founder with strong background in financial modeling, strategic partnerships, and operational scaling. Healthcare go-to-market expert.",
    featured: true
  },
  {
    name: "Yutong Xue",
    role: "CTO & Co-Founder",
    image: "/images/yutong.jpg",
    description: "Deep expertise in AI/ML systems architecture and advanced neural network security. Published researcher in adversarial machine learning.",
    featured: true
  },
  {
    name: "Malak Mohammed",
    role: "AI Engineer",
    image: "/images/malak.jpg",
    description: "Expert in AI/ML development, automation, AI threat detection, and advanced defensive AI techniques for healthcare models."
  },
  {
    name: "Sphesihle Milongo",
    role: "Full Stack Developer",
    image: "/images/siphesihle.png",
    description: "Experienced in React, Node.js, and database design, specializing in EMR/EHR integration for web applications."
  },
  {
    name: "Zamuxolo Nkombisa",
    role: "Junior Full Stack Developer",
    image: "/images/zam.png",
    description: "Focuses on frontend development and API integration with strong emphasis on HIPAA compliance in development."
  }
];

export default function TeamSection() {
  return (
    <section id="team" className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#2dd4bf]/5 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-[#1e3a5f]/5 blur-3xl" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <p className="text-[#1e3a5f] text-sm font-medium tracking-wider uppercase mb-4">Meet The Team</p>
          <h2 className="text-4xl md:text-6xl font-light text-slate-900 mb-6">
            World-Class <span className="bg-gradient-to-r from-[#1e3a5f] to-[#2dd4bf] bg-clip-text text-transparent font-medium">Leadership</span>
          </h2>
          <p className="text-slate-600 max-w-3xl mx-auto text-lg leading-relaxed">
            A balanced blend of strategic vision, deep technical prowess in AI and cybersecurity, 
            and proven execution capability in the healthcare sector.
          </p>
        </motion.div>
        
        {/* Featured Members (Founders) */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {team.filter(member => member.featured).map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, type: "spring", stiffness: 100 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#2dd4bf]/20 to-[#1e3a5f]/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500" />
              
              <div className="relative p-8 rounded-3xl bg-white border-2 border-slate-100 group-hover:border-[#2dd4bf] transition-all duration-300 h-full flex flex-col">
                <div className="relative w-24 h-24 mb-6">
                  {/* Image Container */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
                    <img 
                      src={member.image} 
                      alt={member.name} 
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-500"
                    />
                  </div>
                  {/* Status Indicator */}
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-lg bg-[#2dd4bf] flex items-center justify-center border-4 border-white">
                    <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold text-slate-900 mb-1">{member.name}</h3>
                <p className="text-[#2dd4bf] text-sm font-semibold mb-4 uppercase tracking-wide">{member.role}</p>
                <p className="text-slate-600 leading-relaxed text-sm flex-grow">{member.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Supporting Members */}
        <div className="grid md:grid-cols-3 gap-8">
          {team.filter(member => !member.featured).map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group p-6 rounded-2xl bg-white/60 backdrop-blur-sm border border-slate-200 hover:border-[#1e3a5f]/30 hover:shadow-md transition-all duration-300 flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 rounded-xl overflow-hidden mb-4 border border-slate-200">
                <img 
                  src={member.image} 
                  alt={member.name} 
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
                />
              </div>
              
              <h3 className="text-lg font-medium text-slate-900 mb-1">{member.name}</h3>
              <p className="text-[#1e3a5f] text-sm font-medium mb-3">{member.role}</p>
              <p className="text-slate-500 text-xs leading-relaxed line-clamp-3 group-hover:line-clamp-none transition-all duration-300">
                {member.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}